import React, { useState, useEffect, useMemo, useRef } from 'react';
import Modal from './Modal';
import Button from './Button';
import { Employee, EditableTimeSegment, TimeLog, NameDisplayFormat, Activity } from '../../shared/types.js';
import { formatEmployeeName, toDateInputString, toTimeInputString } from '../../shared/utils.js';
import { LockIcon } from './icons';

interface EntryEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: {
        employeeId: number,
        activityId: string,
        startTime: number,
        endTime?: number | null,
        lunch?: { startTime: number, endTime: number }
    }) => void;
    employees: Employee[];
    initialData: EditableTimeSegment | null;
    activities: Activity[];
    isLocked?: boolean;
    lockTimestamp?: number;
}

const EntryEditorModal: React.FC<EntryEditorModalProps> = ({ isOpen, onClose, onSave, employees, initialData, activities, isLocked = false, lockTimestamp }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const [employeeId, setEmployeeId] = useState<number | ''>('');
    const [activityId, setActivityId] = useState('');
    const [date, setDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [hasStopTime, setHasStopTime] = useState<'yes' | 'no'>('yes');
    const [stopDate, setStopDate] = useState('');
    const [stopTime, setStopTime] = useState('');
    const [insertLunch, setInsertLunch] = useState<'yes' | 'no'>('no');
    const [lunchStartTime, setLunchStartTime] = useState('12:00');
    const [lunchEndTime, setLunchEndTime] = useState('13:00');
    const sortedEmployees = useMemo(() => {
        return [...employees].sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
    }, [employees]);
    const availableActivities = useMemo(() => activities.filter(a => a.name === 'In'), [activities]);
    const inActivityId = useMemo(() => availableActivities.find(a => a.name === 'In')?.id, [availableActivities]);
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                const initialEmployee = employees.find(e => e.id === initialData.employeeId);
                setEmployeeId(initialEmployee?.emp_number || '');
                setActivityId(initialData.activityId);
                setDate(toDateInputString(new Date(initialData.startTime)));
                setStartTime(toTimeInputString(new Date(initialData.startTime)));
                if (initialData.endTime) {
                    setHasStopTime('yes');
                    setStopDate(toDateInputString(new Date(initialData.endTime)));
                    setStopTime(toTimeInputString(new Date(initialData.endTime)));
                } else {
                    setHasStopTime('no');
                    setStopDate('');
                    setStopTime('');
                }
            } else {
                setEmployeeId(sortedEmployees[0]?.emp_number || '');
                setActivityId(inActivityId || '');
                const now = new Date();
                const todayString = toDateInputString(now);
                setDate(todayString);
                setStopDate(todayString);
                setStartTime('08:00');
                setHasStopTime('no');
                setStopTime('16:00');
            }
            setInsertLunch('no');
            setLunchStartTime('12:00');
            setLunchEndTime('13:00');
        }
    }, [initialData, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                formRef.current?.requestSubmit();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onSave]);
    const handleHasStopTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = e.target.value as 'yes' | 'no';
        if (newValue === 'yes' && hasStopTime === 'no') {
            setStopDate(date);
            setStopTime(toTimeInputString(new Date(Date.now())));
        }
        setHasStopTime(newValue);
    };
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!employeeId && !initialData) {
            alert('Please select an employee.');
            return;
        }
        const currentEmployeeId = initialData 
            ? employees.find(e => e.id === initialData.employeeId)?.emp_number
            : employeeId;
        if (!currentEmployeeId) {
            alert('Could not find employee number.');
            return;
        }
        const startTimestamp = new Date(`${date}T${startTime}`).getTime();
        let stopTimestamp: number | null = null;
        if (lockTimestamp && startTimestamp < lockTimestamp) {
            alert(`Cannot ${initialData ? 'edit' : 'add'} an entry to a closed payroll period.`);
            return;
        }
        if (hasStopTime === 'yes') {
            const effectiveStopDate = stopDate || date;
            stopTimestamp = new Date(`${effectiveStopDate}T${stopTime}`).getTime();
            if (startTimestamp >= stopTimestamp) {
                alert("Start time must be before stop time.");
                return;
            }
        }
        let lunchData: { startTime: number, endTime: number } | undefined = undefined;
        if (insertLunch === 'yes') {
            const lunchStartTimestamp = new Date(`${date}T${lunchStartTime}`).getTime();
            const lunchEndTimestamp = new Date(`${date}T${lunchEndTime}`).getTime();
            if (lunchStartTimestamp >= lunchEndTimestamp) {
                alert("Lunch start time must be before lunch end time.");
                return;
            }
            if (lunchStartTimestamp <= startTimestamp) {
                alert("Lunch must start after the work shift begins.");
                return;
            }
            if (stopTimestamp && lunchEndTimestamp >= stopTimestamp) {
                alert("Lunch must end before the work shift ends.");
                return;
            }
            lunchData = { startTime: lunchStartTimestamp, endTime: lunchEndTimestamp };
        }
        onSave({
            employeeId: currentEmployeeId,
            activityId,
            startTime: startTimestamp,
            endTime: stopTimestamp,
            lunch: lunchData
        });
    };
    const renderInputRow = (label: string, id: string, children: React.ReactNode) => (
        <div className="grid grid-cols-1 sm:grid-cols-3 items-start sm:items-center gap-2 sm:gap-4">
            <label htmlFor={id} className="text-left sm:text-right text-gray-600 dark:text-gray-300 font-medium">{label}:</label>
            <div className="sm:col-span-2">{children}</div>
        </div>
    );
    const inputClasses = "block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-gray-900 dark:text-white focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";
    if (isLocked) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} title="View Entry">
                <div className="text-center p-4">
                    <LockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold">Entry Locked</h3>
                    <p className="text-gray-500 mt-2">This entry is in a closed payroll period and cannot be edited.</p>
                     <div className="mt-6">
                        <Button variant="secondary" onClick={onClose}>Close</Button>
                    </div>
                </div>
            </Modal>
        );
    }
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={initialData ? "Edit Entry" : "Add Entry"}>
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 text-gray-900 dark:text-white">
                {initialData ?
                    renderInputRow("Name", "employeeName", 
                        <input id="employeeName" type="text" value={initialData.employeeName} readOnly className={`${inputClasses} bg-gray-100 dark:bg-gray-900/50 cursor-default`} />
                    ) :
                    renderInputRow("Name", "employeeId",
                        <select id="employeeId" value={employeeId} onChange={(e) => setEmployeeId(Number(e.target.value))} required className={inputClasses}>
                            {sortedEmployees.map(emp => (
                                <option key={emp.id} value={emp.emp_number}>{formatEmployeeName(emp, NameDisplayFormat.LAST_FIRST)}</option>
                            ))}
                        </select>
                    )
                }
                {renderInputRow("Start", "startTime",
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center">
						<input id="startTime" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} required className={inputClasses}/>
						<input id="date" type="date" value={date} onChange={e => setDate(e.target.value)} required className={inputClasses}/>
					</div>
                )}
                {renderInputRow("Stop", "hasStopTime",
                    <div className={`grid ${hasStopTime === 'yes' ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1'} gap-2 items-center`}>
                        <select id="hasStopTime" value={hasStopTime} onChange={handleHasStopTimeChange} required className={inputClasses}>
                            <option value="no">Still Working</option>
                            <option value="yes">Has Stop Time</option>
                        </select>
                        {hasStopTime === 'yes' && (
                             <>
                                <input id="stopTime" type="time" value={stopTime} onChange={e => setStopTime(e.target.value)} required className={inputClasses} />
                                <input id="stopDate" type="date" value={stopDate} onChange={e => setStopDate(e.target.value)} required className={inputClasses} />
                            </>
                        )}
                    </div>
                )}
				{renderInputRow("Insert Lunch", "insertLunch",
                    <div className={`grid ${insertLunch === 'yes' ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1'} gap-2 items-center`}>
                        <select id="insertLunch" value={insertLunch} onChange={e => setInsertLunch(e.target.value as 'yes' | 'no')} required className={inputClasses}>
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                        </select>
                        {insertLunch === 'yes' && (
                             <>
                                <input id="lunchStartTime" type="time" value={lunchStartTime} onChange={e => setLunchStartTime(e.target.value)} required={insertLunch === 'yes'} className={inputClasses} />
                                <input id="lunchEndTime" type="time" value={lunchEndTime} onChange={e => setLunchEndTime(e.target.value)} required={insertLunch === 'yes'} className={inputClasses} />
                            </>
                        )}
                    </div>
                )}
                <div className="flex justify-end gap-4 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="submit">Save</Button>
                </div>
            </form>
        </Modal>
    );
};

export default EntryEditorModal;
